import _ from 'lodash'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { useGetMeetingLinkQuery, useUpdateMeetingLinkMutation } from '@/core/services/meeting-link'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getStaffs, selectStaffs } from '@/redux/reducers/staff.reducer'

import MeetingLinkStaffs from '../components/MeetingLinkStaffs'

export default function MeetingLinkStaffsContainer() {
  const dispatch = useAppDispatch()
  const { meetingLinkId } = useParams()

  const authUser = useAppSelector(selectAuthUser)
  const staffs = useAppSelector(selectStaffs)

  const { data: meetingLink } = useGetMeetingLinkQuery(meetingLinkId ?? '', { skip: !meetingLinkId })
  const [updateMeetingLink, { isLoading: isUpdatingMeetingLink }] = useUpdateMeetingLinkMutation()

  const isMeetingLinkCreator = useMemo(() => authUser?._id === meetingLink?.createdBy, [authUser, meetingLink])

  const meetingLinkStaffsData = useMemo(
    () =>
      _.map(isMeetingLinkCreator ? _.filter(meetingLink?.staffs, (staff) => staff._id !== meetingLink?.createdBy) : meetingLink?.staffs, (staff) => ({
        id: staff._id,
        cps: staff.cps || '',
        name: staff.userInfo.name,
        surname: staff.userInfo.surname,
        status: staff.status,
        isSameEtablissement: staff.establishment?._id === authUser?.establishment?._id,
      })),
    [isMeetingLinkCreator, meetingLink, authUser]
  )

  const staffsData = useMemo(
    () =>
      _.map(staffs, (staff) => ({
        id: staff._id,
        cps: staff.cps || '',
        name: staff.userInfo.name,
        surname: staff.userInfo.surname,
        status: staff.status,
        isSameEtablissement: staff.establishment === authUser?.establishment?._id,
      })),
    [staffs, authUser]
  )

  const handleDialogOpen = () => {
    if (!meetingLink) return

    dispatch(getStaffs({ _id: { $nin: _.map(meetingLink.staffs, '_id') }, name: '', surname: '', status: { $in: ['MEDECIN', 'PDS'] } }, { limit: 0 }))
  }

  const handleAddMeetingLinkStaffs = (staffIds: string[]) => {
    if (!meetingLink) return

    updateMeetingLink({ meetingLinkId: meetingLink._id, staffs: _.map(meetingLink.staffs, '_id').concat(staffIds) })
  }

  const handleDeleteMeetingLinkStaffs = (staffIds: string[]) => {
    if (!meetingLink) return

    updateMeetingLink({
      meetingLinkId: meetingLink._id,
      staffs: _.map(
        _.reject(meetingLink.staffs, (staff) => _.includes(staffIds, staff._id)),
        '_id'
      ),
    })
  }

  const handleSearchStaffs = useDebouncedCallback((text: string) => {
    if (!meetingLink) return

    const [name, surname] = text.split(' ')
    dispatch(getStaffs({ _id: { $nin: _.map(meetingLink.staffs, '_id') }, name, surname, status: { $in: ['MEDECIN', 'PDS'] } }, { limit: 0 }))
  }, 300)

  return (
    <MeetingLinkStaffs
      addingStaffs={staffsData}
      isAdding={isUpdatingMeetingLink}
      meetingLinkStaffs={meetingLinkStaffsData}
      onAddMeetingLinkStaffs={handleAddMeetingLinkStaffs}
      onDeleteMeetingLinkStaffs={handleDeleteMeetingLinkStaffs}
      onOpen={handleDialogOpen}
      onSearchStaffs={handleSearchStaffs}
      readOnly={!isMeetingLinkCreator}
    />
  )
}
